import React from 'react';
import type { FlowValidationResult, NodeValidationError } from './types';
import type { WorkflowValidationServerIssue } from '../../../../hooks/useWorkflowExecution';

/**
 * Pure state container for server-returned validation issues.
 *
 * The deterministic validator now lives on the server (`apps/api/src/workflows/validation/`)
 * and is invoked at `POST /workflows/:id/execute`. When validation fails, the
 * server returns `WORKFLOW_VALIDATION_FAILED` carrying `{ issues }`; the
 * Run-flow handler converts it via `setIssuesFromServer` and the existing
 * banner/dialog UI renders the result.
 *
 * No reactive re-validation lives here — gone with the FE validator. Errors
 * stay visible until the user dismisses or until the next Run press refreshes
 * the set.
 */
export function useFlowValidation() {
  const [validationResult, setValidationResult] = React.useState<FlowValidationResult | null>(null);
  const [isValidationActive, setIsValidationActive] = React.useState(false);
  const [isPrevalidating, setIsPrevalidating] = React.useState(false);
  const [currentErrorIndex, setCurrentErrorIndex] = React.useState(0);

  const setIssuesFromServer = React.useCallback(
    (issues: WorkflowValidationServerIssue[]) => {
      const errors: NodeValidationError[] = issues.map((i) => ({
        // The FE error type uses empty string for flow-level (legacy contract).
        nodeId: i.nodeId ?? '',
        nodeType: i.nodeType ?? '',
        field: i.field,
        message: i.message,
        code: i.code,
        severity: i.severity,
      }));
      const errorsByNode = new Map<string, NodeValidationError[]>();
      for (const e of errors) {
        const arr = errorsByNode.get(e.nodeId) ?? [];
        arr.push(e);
        errorsByNode.set(e.nodeId, arr);
      }
      const isValid =
        errors.filter((e) => (e.severity ?? 'error') === 'error').length === 0;
      setValidationResult({ isValid, errors, errorsByNode });
      setIsValidationActive(true);
      setCurrentErrorIndex(0);
    },
    []
  );

  const dismissValidation = React.useCallback(() => {
    setIsValidationActive(false);
    setValidationResult(null);
    setCurrentErrorIndex(0);
  }, []);

  const goToNextError = React.useCallback(() => {
    if (!validationResult) return;
    setCurrentErrorIndex((prev) =>
      prev < validationResult.errors.length - 1 ? prev + 1 : 0
    );
  }, [validationResult]);

  const goToPrevError = React.useCallback(() => {
    if (!validationResult) return;
    setCurrentErrorIndex((prev) =>
      prev > 0 ? prev - 1 : validationResult.errors.length - 1
    );
  }, [validationResult]);

  const getNodeErrors = React.useCallback(
    (nodeId: string): NodeValidationError[] =>
      (isValidationActive && validationResult?.errorsByNode.get(nodeId)) || [],
    [validationResult, isValidationActive]
  );

  const currentError: NodeValidationError | null =
    validationResult?.errors[currentErrorIndex] ?? null;

  return {
    validationResult,
    isValidationActive,
    isPrevalidating,
    setIsPrevalidating,
    currentErrorIndex,
    currentError,
    setIssuesFromServer,
    dismissValidation,
    goToNextError,
    goToPrevError,
    getNodeErrors,
  };
}
